import {
  Button,
  Card,
  CardBody,
  Chip,
  Skeleton,
  useDisclosure,
} from '@nextui-org/react';
import { Order, PartnerInfo } from '../types';
import React, { useEffect, useState } from 'react';

import CreateOrderModal from './CreateOrderModal';
import { fetchOrders } from '../services/orderService';
import { fetchPartnerInfo } from '../services/resellerService';
import moment from 'moment';
import { useAuth0 } from '@auth0/auth0-react';

const StatCard: React.FC<{
  label: string;
  value: string | number;
  sublabel?: string;
}> = ({ label, value, sublabel }) => (
  <Card className="bg-white border border-slate-200 shadow-none">
    <CardBody className="p-5">
      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">
        {label}
      </p>
      <p className="text-2xl font-bold text-slate-800">{value}</p>
      {sublabel && (
        <p className="text-xs text-slate-400 mt-1">{sublabel}</p>
      )}
    </CardBody>
  </Card>
);

const getFulfillmentStatus = (order: Order) => {
  if (order.fulfillment_status === 'fulfilled') return { label: 'Fulfilled', color: 'success' as const };
  if (order.fulfillments && order.fulfillments.length > 0) return { label: 'In Transit', color: 'warning' as const };
  return { label: 'Processing', color: 'default' as const };
};

const Orders: React.FC = () => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const [orders, setOrders] = useState<Order[]>([]);
  const [partnerInfo, setPartnerInfo] = useState<PartnerInfo>();
  const [error, setError] = useState<string | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isLoaded, setIsLoaded] = useState(false);
  const [isSingleTrialUnit, setIsSingleTrialUnit] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!isAuthenticated) return;
      try {
        const token = await getAccessTokenSilently();
        const [ordersData, partnerData] = await Promise.all([
          fetchOrders(token),
          fetchPartnerInfo(token),
        ]);
        setOrders(ordersData);
        setPartnerInfo(partnerData);
        setIsLoaded(true);
      } catch (error: any) {
        setError(error?.message || 'An error occurred');
      }
    };
    fetchData();
  }, [getAccessTokenSilently, isAuthenticated]);

  const handleCreateOrder = async (
    quantity: number,
    discount_percentage: number,
    discount_name: string
  ) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/draft-order`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            variant_id: 44224232554675,
            quantity,
            discount_percentage,
            discount_name,
            customer_id: partnerInfo?.shopify_customer_id ?? '',
          }),
        }
      );
      if (!response.ok) throw new Error('Failed to create draft order');
      const data = await response.json();
      if (data.invoice_url) {
        window.location.href = data.invoice_url;
      } else {
        setError('No invoice URL found in the response');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred while creating the order');
    }
  };

  const totalRevenue = orders.reduce(
    (sum, order) => sum + parseFloat(order.total_price || '0'),
    0
  );

  const totalUnits = orders.reduce(
    (sum, order) =>
      sum + order.line_items.reduce((s, item) => s + item.quantity, 0),
    0
  );

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <Skeleton isLoaded={isLoaded} className="rounded-lg">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">
              Welcome back, {partnerInfo?.first_name}
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {partnerInfo?.company_name} &mdash; Authorized Partner
            </p>
          </div>
        </Skeleton>
        <Skeleton isLoaded={isLoaded} className="rounded-lg">
          <Button
            color="primary"
            onClick={() => {
              onOpen();
              setIsSingleTrialUnit(false);
            }}
            className="bg-airlock-purple hover:bg-airlock-deep text-white font-medium"
            radius="sm"
            size="md"
          >
            + New Order
          </Button>
        </Skeleton>
      </div>

      {error && (
        <Card className="mb-6 bg-red-50 border border-red-200 shadow-none">
          <CardBody className="p-4">
            <p className="text-sm text-red-700">{error}</p>
          </CardBody>
        </Card>
      )}

      <Skeleton isLoaded={isLoaded} className="rounded-lg mb-8">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <StatCard label="Total Orders" value={orders.length} sublabel="Lifetime" />
          <StatCard label="Total Units" value={totalUnits} sublabel="All orders combined" />
          <StatCard
            label="Total Revenue"
            value={`$${totalRevenue.toLocaleString('en-US', { minimumFractionDigits: 2 })}`}
            sublabel="Partner pricing"
          />
        </div>
      </Skeleton>

      <div className="mb-4">
        <h2 className="text-lg font-semibold text-slate-700">Order History</h2>
        <p className="text-xs text-slate-400 mt-1">
          All orders placed through your partner account
        </p>
      </div>

      <div className="space-y-3">
        {orders.length > 0 ? (
          orders.map(order => {
            const status = getFulfillmentStatus(order);
            return (
              <Card
                key={order.id}
                className="bg-white border border-slate-200 shadow-none hover:border-slate-300 transition-colors"
              >
                <CardBody className="p-5">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-sm font-bold text-slate-800">
                          #{order.order_number}
                        </h3>
                        <Chip size="sm" variant="flat" color={status.color} className="text-xs">
                          {status.label}
                        </Chip>
                        <span className="text-xs text-slate-400">
                          {moment(order.created_at).format('MMM D, YYYY')}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600">
                        {order.line_items[0]?.name} &times; {order.line_items[0]?.quantity}
                      </p>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4 lg:gap-8 text-sm">
                      <div>
                        <p className="text-xs text-slate-400">Ship To</p>
                        <p className="text-slate-600">
                          {order?.shipping_address?.city && order?.shipping_address?.province
                            ? `${order.shipping_address.city}, ${order.shipping_address.province}`
                            : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400">Tracking</p>
                        <p className="text-slate-600 font-mono text-xs">
                          {order.fulfillments?.[0]?.tracking_number || 'Pending'}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-slate-400">Total</p>
                        <p className="text-slate-800 font-semibold">
                          ${parseFloat(order.total_price).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardBody>
              </Card>
            );
          })
        ) : (
          <Skeleton isLoaded={isLoaded} className="rounded-lg">
            <Card className="bg-white border border-slate-200 shadow-none">
              <CardBody className="p-8 text-center">
                <p className="text-slate-500 text-sm">
                  No orders yet. Place your first order to get started.
                </p>
              </CardBody>
            </Card>
          </Skeleton>
        )}
      </div>

      <CreateOrderModal
        isOpen={isOpen}
        onClose={onClose}
        isSingleTrialUnit={isSingleTrialUnit}
        partnerInfo={partnerInfo}
        onSubmit={(qty: number, discount_percentage: number, discount_name: string) =>
          handleCreateOrder(qty, discount_percentage, discount_name)
        }
      />
    </div>
  );
};

export default Orders;
